import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, ShieldCheck, Clock3, XCircle, ScanSearch, ArrowRight, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { FileDropzone } from '@/components/ui/file-dropzone';
import { ModalViewerDetail } from '@/components/ui/modal-viewer-detail';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { FieldLabel, SegmentedControl, SECTION_CLS, SECTION_HEADER_CLS } from '@/components/ui/modal-form';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import {
  acceptDeTipo,
  analizarDocumentoPersona,
  archivoABase64,
  avisoDeSubida,
  ayudaDeTipo,
  esIdentificacion,
  esTipoAnexo,
  OPCIONES_IDENTIFICACION,
  subirDocumentoPersona,
  tiposSubibles,
  type AnalisisDocumento,
  type CambioARevision,
  type ResultadoSubida,
} from '@/lib/documentos/subir-documento-persona';
import { INE_COMPLETO_TIPO_ID } from '@/utils/expediente-obligatorios';

/**
 * Modal ÚNICO para subir un documento al expediente de una persona, en el admin y
 * en los portales. Sigue el flujo de la app de cliente:
 *
 *   1. Se elige el tipo. La identificación es UNA opción con dos formatos (INE
 *      completo o pasaporte), nunca Frente/Reverso por separado.
 *   2. Al elegir el archivo se revisa en el servidor (`analizar`) SIN guardarlo y
 *      se dice qué va a pasar: se valida solo, queda en revisión o se rechaza.
 *   3. Lo que se leyó (CURP, RFC, régimen, domicilio fiscal...) se ve junto al
 *      archivo y se puede corregir. Lo que llena un dato vacío se escribe en la
 *      persona al subir; lo que CAMBIA un dato ya registrado va a una solicitud
 *      que aprueba un Super Administrador (Autorizaciones de datos).
 *   4. Antes de subir se confirma: "¿Estás seguro de que la información es correcta?".
 *
 * Las reglas no viven aquí: son las de `expediente-documento`, las mismas que la app.
 */

export interface TipoDocumentoOpcion {
  id: number;
  nombre: string;
}

export interface SubirDocumentoPersonaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  idPersona: number;
  /** Nombre de la persona, para el subtítulo. */
  nombrePersona?: string | null;
  /** Catálogo de tipos ofrecibles. Los legacy (Frente/Reverso INE) se quitan solos. */
  tipos: TipoDocumentoOpcion[];
  /** Tipo preseleccionado (p. ej. el slot del expediente que se está llenando). */
  tipoInicial?: number | null;
  /** Fija el tipo: no se muestra el selector. */
  tipoFijo?: boolean;
  /** Liga el documento también a una cuenta de cobranza. */
  idCuentaCobranza?: number | null;
  /** Pide el número del documento (folio, número de escritura...). */
  conNumero?: boolean;
  onSubido?: (resultado: ResultadoSubida, tipoId: number) => void;
  /**
   * Solo en la sección de Perfil de la persona: muestra los datos leídos (CSF, CURP...)
   * y los aplica a la persona. En cualquier otra pantalla el documento se guarda como
   * archivo y no toca sus datos (p. ej. una CSF subida para facturar).
   */
  capturaDatos?: boolean;
}

const VALOR_IDENTIFICACION = 'identificacion';

export function SubirDocumentoPersonaDialog({
  open,
  onOpenChange,
  idPersona,
  nombrePersona,
  tipos,
  tipoInicial = null,
  tipoFijo = false,
  idCuentaCobranza = null,
  conNumero = false,
  onSubido,
  capturaDatos = false,
}: SubirDocumentoPersonaDialogProps) {
  const { toast } = useToast();
  const [tipoId, setTipoId] = useState<number | null>(tipoInicial);
  const [file, setFile] = useState<File | null>(null);
  const [base64, setBase64] = useState<string | null>(null);
  const [analisis, setAnalisis] = useState<AnalisisDocumento | null>(null);
  const [analizando, setAnalizando] = useState(false);
  const [errorAnalisis, setErrorAnalisis] = useState<string | null>(null);
  const [valores, setValores] = useState<Record<string, string>>({});
  const [aplicarCampos, setAplicarCampos] = useState(capturaDatos);
  const [numero, setNumero] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [subiendo, setSubiendo] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  // Solo cuenta la respuesta del último análisis: cambiar de archivo o de tipo
  // mientras uno corre no debe pintar el resultado viejo.
  const turno = useRef(0);

  useEffect(() => {
    if (!open) return;
    setTipoId(tipoInicial);
    setFile(null);
    setBase64(null);
    setAnalisis(null);
    setErrorAnalisis(null);
    setValores({});
    setAplicarCampos(true);
    setNumero('');
    setDescripcion('');
    setConfirmando(false);
  }, [open, tipoInicial]);

  // Vista previa del archivo elegido: la URL local se libera al cambiarlo o cerrar.
  const urlArchivo = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => {
    if (urlArchivo) URL.revokeObjectURL(urlArchivo);
  }, [urlArchivo]);

  const subibles = useMemo(() => tiposSubibles(tipos), [tipos]);
  const hayIdentificacion = subibles.some((t) => esIdentificacion(t.id));

  const opciones = useMemo(() => {
    const out = subibles
      .filter((t) => !esIdentificacion(t.id))
      .map((t) => ({ value: String(t.id), label: t.nombre }));
    if (hayIdentificacion) {
      out.push({ value: VALOR_IDENTIFICACION, label: 'Identificación oficial (INE o pasaporte)' });
    }
    return out.sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));
  }, [subibles, hayIdentificacion]);

  const valorSelect = tipoId == null ? '' : esIdentificacion(tipoId) ? VALOR_IDENTIFICACION : String(tipoId);
  const nombreTipo = tipoId != null && esIdentificacion(tipoId)
    ? 'Identificación oficial'
    : subibles.find((t) => t.id === tipoId)?.nombre ?? 'Documento';

  async function analizar(tipo: number, f: File) {
    const mio = ++turno.current;
    setAnalizando(true);
    setAnalisis(null);
    setErrorAnalisis(null);
    try {
      const b64 = await archivoABase64(f);
      if (mio !== turno.current) return;
      setBase64(b64);
      const r = await analizarDocumentoPersona({ idPersona, tipoId: tipo, file: f, base64: b64 });
      if (mio !== turno.current) return;
      setAnalisis(r);
      setValores(Object.fromEntries(r.campos.map((c) => [c.key, c.valor ?? ''])));
    } catch (e) {
      if (mio !== turno.current) return;
      setErrorAnalisis((e as Error).message);
    } finally {
      if (mio === turno.current) setAnalizando(false);
    }
  }

  function cambiarTipo(nuevo: number | null) {
    setTipoId(nuevo);
    // El archivo elegido puede no valer para el tipo nuevo (foto en una CSF):
    // se vuelve a revisar contra el tipo nuevo.
    if (nuevo != null && file) void analizar(nuevo, file);
  }

  function cambiarArchivo(f: File | null) {
    setFile(f);
    setAnalisis(null);
    setErrorAnalisis(null);
    setBase64(null);
    turno.current++;
    setAnalizando(false);
    if (f && tipoId != null) void analizar(tipoId, f);
  }

  const rechazado = analisis?.resultado === 'rechazado';
  const anexo = esTipoAnexo(tipoId);
  const puedeSubir = !!file && tipoId != null && !!analisis && !rechazado && !analizando && !subiendo &&
    (!anexo || !!descripcion.trim());

  const cambiosARevision: CambioARevision[] = aplicarCampos ? analisis?.cambios_a_revision ?? [] : [];

  async function subir() {
    if (!file || tipoId == null || !analisis) return;
    setConfirmando(false);
    setSubiendo(true);
    try {
      const campos = capturaDatos && aplicarCampos && analisis.campos.length
        ? Object.fromEntries(Object.entries(valores).filter(([, v]) => v.trim()))
        : undefined;
      const r = await subirDocumentoPersona({
        idPersona,
        tipoId,
        file,
        base64: base64 ?? undefined,
        hash: analisis.hash,
        campos,
        origen: 'perfil',
        idCuentaCobranza,
        numero: conNumero && numero.trim() ? numero.trim() : null,
        descripcion: anexo ? descripcion.trim() : null,
      });
      const aviso = avisoDeSubida(r);
      toast({ title: aviso.titulo, description: aviso.detalle, variant: aviso.advertencia ? 'destructive' : undefined });
      onSubido?.(r, tipoId);
      onOpenChange(false);
    } catch (e) {
      toast({ variant: 'destructive', title: 'No se subió el documento', description: (e as Error).message });
    } finally {
      setSubiendo(false);
    }
  }

  return (
    <ModalViewerDetail
      open={open}
      onOpenChange={(v) => !subiendo && onOpenChange(v)}
      title="Subir documento"
      subtitle={nombrePersona ? `Expediente de ${nombrePersona}` : undefined}
      // Estándar de cobranza: el archivo a la izquierda y el formulario a la derecha.
      resource={<VistaPreviaArchivo file={file} url={urlArchivo} />}
      resourceClassName="h-[45vh] md:h-[85vh]"
      gridClassName="md:grid-cols-[1.2fr_1fr]"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={subiendo}>
            Cancelar
          </Button>
          <Button onClick={() => setConfirmando(true)} disabled={!puedeSubir}>
            {subiendo && <Loader2 className="mr-2 size-4 animate-spin" />}
            Subir documento
          </Button>
        </>
      }
    >
      {!tipoFijo && (
        <div>
          <FieldLabel required>Tipo de documento</FieldLabel>
          <SearchableSelect
            value={valorSelect}
            onValueChange={(v) =>
              cambiarTipo(v === VALOR_IDENTIFICACION ? INE_COMPLETO_TIPO_ID : v ? Number(v) : null)
            }
            options={opciones}
            placeholder="Selecciona el tipo"
            itemsLabel="tipos de documento"
          />
        </div>
      )}

      {tipoId != null && esIdentificacion(tipoId) && (
        <div>
          <FieldLabel required>Identificación</FieldLabel>
          <SegmentedControl<string>
            value={String(tipoId)}
            onChange={(v) => cambiarTipo(Number(v))}
            options={OPCIONES_IDENTIFICACION.map((o) => ({ value: String(o.tipoId), label: o.label }))}
          />
        </div>
      )}

      {tipoId != null && (
        <div>
          <FieldLabel required>{tipoFijo ? nombreTipo : 'Archivo'}</FieldLabel>
          <FileDropzone
            value={file}
            onChange={cambiarArchivo}
            accept={acceptDeTipo(tipoId)}
            maxMb={10}
            hint={ayudaDeTipo(tipoId)}
            disabled={subiendo}
          />
          <p className="mt-1.5 text-[11px] text-muted-foreground">{ayudaDeTipo(tipoId)}</p>
        </div>
      )}

      {(analizando || analisis || errorAnalisis) && (
        <ResultadoAnalisis analizando={analizando} analisis={analisis} error={errorAnalisis} />
      )}

      {capturaDatos && analisis && !rechazado && analisis.campos.length > 0 && (
        <div className={SECTION_CLS}>
          <p className={SECTION_HEADER_CLS}>Datos leídos del documento</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {analisis.campos.map((c) => (
              <div key={c.key} className={cn(c.key === 'nombre' || c.key === 'calle' ? 'sm:col-span-2' : '')}>
                <FieldLabel>{c.label}</FieldLabel>
                <Input
                  value={valores[c.key] ?? ''}
                  onChange={(e) => setValores((p) => ({ ...p, [c.key]: e.target.value }))}
                  className={cn(c.mono && 'font-mono uppercase')}
                  disabled={!aplicarCampos || c.solo_lectura}
                />
              </div>
            ))}
          </div>
          <label className="mt-3 flex items-center gap-2 text-sm text-foreground">
            <Checkbox checked={aplicarCampos} onCheckedChange={(v) => setAplicarCampos(v === true)} />
            Actualizar los datos de la persona con estos valores
          </label>
          {cambiosARevision.length > 0 && <AvisoCambiosARevision cambios={cambiosARevision} />}
        </div>
      )}

      {anexo && (
        <div>
          <FieldLabel required>Descripción del anexo</FieldLabel>
          <Input
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            maxLength={120}
            placeholder="Ej. Reforma de estatutos 2024"
          />
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            Se pueden tener varios anexos: la descripción es lo que los distingue.
          </p>
        </div>
      )}

      {conNumero && tipoId != null && (
        <div>
          <FieldLabel hint="(opcional)">Número del documento</FieldLabel>
          <Input value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="Folio o número" />
        </div>
      )}

      <AlertDialog open={confirmando} onOpenChange={setConfirmando}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro de que la información es correcta?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  Vas a subir <span className="font-medium text-foreground">{nombreTipo}</span>
                  {nombrePersona ? <> al expediente de <span className="font-medium text-foreground">{nombrePersona}</span></> : null}.
                  Revisa que el documento sea de esta persona y que los datos leídos coincidan con él.
                </p>
                {cambiosARevision.length > 0 && (
                  <p>
                    Los datos que cambian lo ya registrado no se aplican ahora: se enviarán a revisión y
                    se actualizarán cuando los apruebe un Super Administrador.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Revisar de nuevo</AlertDialogCancel>
            <AlertDialogAction onClick={() => void subir()}>Sí, subir documento</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ModalViewerDetail>
  );
}

/** El archivo elegido, tal cual se va a subir: PDF en el visor nativo, imagen como imagen. */
function VistaPreviaArchivo({ file, url }: { file: File | null; url: string | null }) {
  if (!file || !url) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 py-16 text-center">
        <FileText className="size-9 text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground">Aquí verás el documento al elegir el archivo.</p>
      </div>
    );
  }
  if (file.type.startsWith('image/')) {
    return (
      <div className="flex h-full items-center justify-center overflow-auto p-4">
        <img src={url} alt={`Vista previa de ${file.name}`} className="max-h-full max-w-full object-contain" />
      </div>
    );
  }
  return <iframe src={url} title={`Vista previa de ${file.name}`} className="h-full w-full border-0" />;
}

/** Lo leído que cambia datos ya registrados: no se aplica al subir, va a revisión. */
function AvisoCambiosARevision({ cambios }: { cambios: CambioARevision[] }) {
  return (
    <div className="mt-3 rounded-lg border border-warning/30 bg-warning/5 px-3.5 py-3 text-sm">
      <p className="font-medium text-warning">Estos datos cambian lo que ya está registrado y pasarán a revisión</p>
      <ul className="mt-1.5 space-y-1 text-muted-foreground">
        {cambios.map((c) => (
          <li key={c.campo} className="flex flex-wrap items-center gap-1.5">
            <span className="font-medium text-foreground">{c.etiqueta}:</span>
            <span>{c.actual?.trim() || 'Sin dato'}</span>
            <ArrowRight className="size-3.5 shrink-0" />
            <span className="font-medium text-foreground">{c.nuevo || 'Sin dato'}</span>
          </li>
        ))}
      </ul>
      <p className="mt-1.5 text-[11px] text-muted-foreground">
        Se aplicarán cuando los apruebe un Super Administrador en Autorizaciones de datos.
      </p>
    </div>
  );
}

function ResultadoAnalisis({
  analizando,
  analisis,
  error,
}: {
  analizando: boolean;
  analisis: AnalisisDocumento | null;
  error: string | null;
}) {
  if (analizando) {
    return (
      <div className="flex items-center gap-2.5 rounded-lg border border-border bg-muted/30 px-3.5 py-3 text-sm text-muted-foreground">
        <Loader2 className="size-4 shrink-0 animate-spin" />
        Revisando el documento… puede tardar unos segundos.
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-start gap-2.5 rounded-lg border border-destructive/30 bg-destructive/5 px-3.5 py-3 text-sm">
        <XCircle className="mt-0.5 size-4 shrink-0 text-destructive" />
        <div>
          <p className="font-medium text-destructive">No se pudo revisar el documento</p>
          <p className="mt-0.5 text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }
  if (!analisis) return null;

  const cfg = analisis.resultado === 'valido'
    ? {
      icon: ShieldCheck,
      wrap: 'border-success/30 bg-success/5',
      color: 'text-success',
      titulo: 'Se validará automáticamente',
      detalle: analisis.motivo ?? 'El documento cumple las reglas: quedará como Validado al subirlo.',
    }
    : analisis.resultado === 'rechazado'
    ? {
      icon: XCircle,
      wrap: 'border-destructive/30 bg-destructive/5',
      color: 'text-destructive',
      titulo: 'No se puede subir',
      detalle: analisis.motivo ?? 'El documento no es válido.',
    }
    : {
      icon: Clock3,
      wrap: 'border-warning/30 bg-warning/5',
      color: 'text-warning',
      titulo: 'Quedará en revisión manual',
      detalle: analisis.motivo ?? 'No se pudo validar automáticamente.',
    };
  const Icon = cfg.icon;
  return (
    <div className={cn('flex items-start gap-2.5 rounded-lg border px-3.5 py-3 text-sm', cfg.wrap)}>
      <Icon className={cn('mt-0.5 size-4 shrink-0', cfg.color)} />
      <div className="min-w-0">
        <p className={cn('font-medium', cfg.color)}>{cfg.titulo}</p>
        <p className="mt-0.5 text-muted-foreground">{cfg.detalle}</p>
        {analisis.revisado_con_ia && (
          <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground">
            {/* No se nombra la herramienta: quien sube solo debe saber que se analizo. */}
            <ScanSearch className="size-3" /> Analizamos tu información: legibilidad, que esté completo y vigente
          </p>
        )}
      </div>
    </div>
  );
}
